// @core/stores/analytics/AnalyticsStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { analyticsApi } from '@core/api';
import type {
  CreateDashboardRequest,
  CreateReportScheduleRequest,
  CreateSavedQueryRequest,
  CreateWidgetRequest,
  DashboardResponse,
  DuplicateDashboardRequest,
  GenerateReportRequest,
  ListAvailableDataSourcesResponse,
  ListDashboardsResponse,
  ListReportSchedulesResponse,
  ListSavedQueriesResponse,
  QueryAggregationRequest,
  QueryAggregationResponse,
  QueryDataRequest,
  QueryDataResponse,
  QueryTimeSeriesRequest,
  QueryTimeSeriesResponse,
  Report,
  ReportResponse,
  ReportScheduleResponse,
  SavedQueryResponse,
  ShareDashboardRequest,
  UpdateDashboardRequest,
  UpdateReportScheduleRequest,
  UpdateSavedQueryRequest,
  UpdateWidgetRequest,
  WidgetResponse,
} from '@core/types/analytics';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AnalyticsStore {
  // List state
  items: Report[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Report | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Report>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Report | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Report | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create a new dashboard
   */
  async CreateDashboard(data: CreateDashboardRequest): Promise<DashboardResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.CreateDashboard(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create report schedule
   */
  async CreateReportSchedule(data: CreateReportScheduleRequest): Promise<ReportScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.CreateReportSchedule(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create saved query
   */
  async CreateSavedQuery(data: CreateSavedQueryRequest): Promise<SavedQueryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.CreateSavedQuery(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create widget
   */
  async CreateWidget(dashboard_id: string, data: CreateWidgetRequest): Promise<WidgetResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.CreateWidget(dashboard_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete dashboard
   */
  async DeleteDashboard(dashboard_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await analyticsApi.DeleteDashboard(dashboard_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== dashboard_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === dashboard_id) {
          this.currentItem = null;
        }

        this.removeFromCache(dashboard_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete report
   */
  async DeleteReport(report_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await analyticsApi.DeleteReport(report_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== report_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === report_id) {
          this.currentItem = null;
        }

        this.removeFromCache(report_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete report schedule
   */
  async DeleteReportSchedule(schedule_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await analyticsApi.DeleteReportSchedule(schedule_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== schedule_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === schedule_id) {
          this.currentItem = null;
        }

        this.removeFromCache(schedule_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete saved query
   */
  async DeleteSavedQuery(query_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await analyticsApi.DeleteSavedQuery(query_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== query_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === query_id) {
          this.currentItem = null;
        }

        this.removeFromCache(query_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete widget
   */
  async DeleteWidget(widget_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await analyticsApi.DeleteWidget(widget_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== widget_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === widget_id) {
          this.currentItem = null;
        }

        this.removeFromCache(widget_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Duplicate dashboard
   */
  async DuplicateDashboard(dashboard_id: string, data: DuplicateDashboardRequest): Promise<DashboardResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.DuplicateDashboard(dashboard_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate report
   */
  async GenerateReport(data: GenerateReportRequest): Promise<ReportResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.GenerateReport(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get dashboard by ID
   */
  async GetDashboard(dashboard_id: string): Promise<DashboardResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.GetDashboard(dashboard_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get report
   */
  async GetReport(report_id: string): Promise<Report> {
    // Check cache first
    const cached = this.cache.get(report_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await analyticsApi.GetReport(report_id);
      const item = response.report;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get saved query
   */
  async GetSavedQuery(query_id: string): Promise<SavedQueryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.GetSavedQuery(query_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get widget
   */
  async GetWidget(widget_id: string): Promise<WidgetResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.GetWidget(widget_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List available data sources
   */
  async ListAvailableDataSources(params?: PaginatedRequest): Promise<ListAvailableDataSourcesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.ListAvailableDataSources(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List dashboards
   */
  async ListDashboards(params?: PaginatedRequest): Promise<ListDashboardsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.ListDashboards(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List report schedules
   */
  async ListReportSchedules(params?: PaginatedRequest): Promise<ListReportSchedulesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.ListReportSchedules(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List reports
   */
  async ListReports(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await analyticsApi.ListReports(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List saved queries
   */
  async ListSavedQueries(params?: PaginatedRequest): Promise<ListSavedQueriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.ListSavedQueries(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Query aggregated data
   */
  async QueryAggregation(data: QueryAggregationRequest): Promise<QueryAggregationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.QueryAggregation(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Query analytics data
   */
  async QueryData(data: QueryDataRequest): Promise<QueryDataResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.QueryData(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Query time series data
   */
  async QueryTimeSeries(data: QueryTimeSeriesRequest): Promise<QueryTimeSeriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.QueryTimeSeries(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Share dashboard
   */
  async ShareDashboard(dashboard_id: string, data: ShareDashboardRequest): Promise<DashboardResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.ShareDashboard(dashboard_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update dashboard
   */
  async UpdateDashboard(dashboard_id: string, data: UpdateDashboardRequest): Promise<DashboardResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.UpdateDashboard(dashboard_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update report schedule
   */
  async UpdateReportSchedule(schedule_id: string, data: UpdateReportScheduleRequest): Promise<ReportScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.UpdateReportSchedule(schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update saved query
   */
  async UpdateSavedQuery(query_id: string, data: UpdateSavedQueryRequest): Promise<SavedQueryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.UpdateSavedQuery(query_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update widget
   */
  async UpdateWidget(widget_id: string, data: UpdateWidgetRequest): Promise<WidgetResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await analyticsApi.UpdateWidget(widget_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const analyticsStore = new AnalyticsStore();
